<script setup>
import { computed, onMounted, shallowRef } from 'vue';
import { useRouter } from 'vue-router';
import { useDisplay } from 'vuetify';
import moment from 'moment';
import { apiRequest } from '@/api/http';
import { useBranding } from '@/branding';
import { useAppearance } from '@/composables/useAppearance';
import { formatDate } from '@/utils/format';
import UiBadge from '@/components/ui/UiBadge.vue';
import UiChoiceCards from '@/components/ui/UiChoiceCards.vue';
import UiDialog from '@/components/ui/UiDialog.vue';
import UiEmptyState from '@/components/ui/UiEmptyState.vue';

// Public home page at "/": the school (from School Settings), a way into the
// parent or faculty portal, the school's active events and DepEd's mission,
// vision and core values.
const branding = useBranding();
const router = useRouter();
// Visitors aren't signed in, so there's no Settings page to reach; one
// button flips between light and dark (the choice is kept per device).
const { isDark, setMode } = useAppearance();

const PORTALS = [
  {
    id: 0,
    title: 'Parent / Guardian',
    icon: 'mdi-account-child-outline',
    description: "Follow your child's grades, attendance and school updates.",
    image: new URL('@/assets/img/parenting.jpg', import.meta.url).href,
  },
  {
    id: 1,
    title: 'Admin / Faculty',
    icon: 'mdi-human-male-board',
    description: 'Class records, attendance, schedules and reports.',
    image: new URL('@/assets/img/admin_teacher.jpg', import.meta.url).href,
  },
];

const HIGHLIGHTS = [
  { icon: 'mdi-notebook-outline', title: 'Class records', text: 'Grades, transmutation and report cards.' },
  { icon: 'mdi-calendar-check-outline', title: 'Attendance', text: 'Daily attendance with early dropout alerts.' },
  { icon: 'mdi-account-child-outline', title: 'For parents', text: 'Grades, attendance and reports at a glance.' },
];

const MISSION =
  'To protect and promote the right of every Filipino to quality, equitable, culture-based, and complete basic education where students learn in a child-friendly, gender-sensitive, safe, and motivating environment; teachers facilitate learning and constantly nurture every learner; administrators and staff ensure an enabling and supportive environment; and family, community, and other stakeholders share responsibility for developing life-long learners.';
const VISION =
  'We dream of Filipinos who passionately love their country and whose values and competencies enable them to realize their full potential and contribute meaningfully to building the nation. As a learner-centered public institution, the Department of Education continuously improves itself to better serve its stakeholders.';
const CORE_VALUES = [
  { name: 'Maka-Diyos', meaning: 'Love of God', icon: 'mdi-hands-pray' },
  { name: 'Maka-tao', meaning: 'Love of people', icon: 'mdi-account-group-outline' },
  { name: 'Makakalikasan', meaning: 'Love of nature', icon: 'mdi-leaf' },
  { name: 'Makabansa', meaning: 'Love of country', icon: 'mdi-flag-variant-outline' },
];

// The photo from School Settings › Landing page, else the bundled one.
const heroPhoto = computed(() => {
  const hero = branding.assets['landing-hero'];
  return hero?.custom ? hero.url : '/img/landingpage.jpg';
});
const place = computed(() => [branding.address, branding.division].filter(Boolean).join(' · '));
const year = new Date().getFullYear();

// --- Portal picker ------------------------------------------------------
const portalOpen = shallowRef(false);
// Remembered for sign-up's account type (0 = parent, 1 = faculty).
function openPortal(portal) {
  localStorage.setItem('userType', portal.id);
  portalOpen.value = false;
  router.push('/login');
}

// --- Events ---------------------------------------------------------------
// The API groups active events by their old type ({ event: { Academic:
// [...], … } }); the page shows them together.
const grouped = shallowRef({});
const loadingEvents = shallowRef(true);
const eventsFailed = shallowRef(false);

onMounted(async () => {
  try {
    grouped.value = (await apiRequest('get', '/school-events/getEventsWithMandatory'))?.event ?? {};
  } catch {
    eventsFailed.value = true;
  } finally {
    loadingEvents.value = false;
  }
});

const today = moment().startOf('day');
const allEvents = computed(() =>
  Object.entries(grouped.value).flatMap(([type, list]) =>
    (list || []).map((e, i) => ({
      ...e,
      key: `${type}-${i}`,
      past: moment(e.eventDate).isBefore(today),
      eventFile: e.eventFile ? `${process.env.VUE_APP_SERVER}/school-events/getEventImg/${e.eventFile}` : null,
    })),
  ),
);
// Upcoming events go in the carousel, soonest first; past ones in the strip
// below, most recent first.
const upcoming = computed(() =>
  allEvents.value.filter((e) => !e.past).sort((a, b) => moment(a.eventDate) - moment(b.eventDate)),
);
const PAST_SHOWN = 8;
const pastEvents = computed(() =>
  allEvents.value
    .filter((e) => e.past)
    .sort((a, b) => moment(b.eventDate) - moment(a.eventDate))
    .slice(0, PAST_SHOWN),
);

function untilLabel(date) {
  const days = moment(date).startOf('day').diff(today, 'days');
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  return `In ${days} days`;
}

// The carousel moves on by itself, but holds still while the pointer or
// keyboard focus is on it, and for visitors who asked for less motion.
const { smAndDown } = useDisplay();
const slide = shallowRef(0);
const hovering = shallowRef(false);
const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
const autoplay = computed(() => !reduceMotion && !hovering.value && upcoming.value.length > 1);
function step(by) {
  const n = upcoming.value.length;
  slide.value = (slide.value + by + n) % n;
}

// --- School announcements (posted by the superadmin) ---------------------
const announcements = shallowRef([]);
onMounted(async () => {
  try {
    const rows = await apiRequest('get', '/school-announcements');
    announcements.value = Array.isArray(rows) ? rows : [];
  } catch {
    // The section just stays hidden; events still load.
  }
});
// The first (pinned, else newest) leads; the next few sit beside it and the
// rest open with "Show all".
const NOTICES_SHOWN = 4;
const showAllNotices = shallowRef(false);
const leadNotice = computed(() => announcements.value[0] ?? null);
const otherNotices = computed(() => {
  const rest = announcements.value.slice(1);
  return showAllNotices.value ? rest : rest.slice(0, NOTICES_SHOWN);
});
const hiddenNotices = computed(() => Math.max(announcements.value.length - 1 - NOTICES_SHOWN, 0));
const isNew = (a) => moment().diff(moment(a.createdAt), 'days') < 7;

const reading = shallowRef(null);
const readingOpen = shallowRef(false);
function openAnnouncement(a) {
  reading.value = a;
  readingOpen.value = true;
}

const selected = shallowRef(null);
const detailOpen = shallowRef(false);
function openEvent(event) {
  selected.value = event;
  detailOpen.value = true;
}

function scrollTo(id) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}
</script>

<template>
  <div class="landing">
    <!-- Top bar -->
    <header class="landing__bar">
      <div class="landing__container landing__bar-inner">
        <router-link to="/" class="landing__brand">
          <img :src="branding.logoUrl" alt="" class="landing__logo" />
          <span class="landing__brand-name">{{ branding.name }}</span>
        </router-link>
        <nav class="landing__nav" aria-label="Sections">
          <button v-if="leadNotice" type="button" class="landing__nav-link" @click="scrollTo('announcements')">
            Announcements
          </button>
          <button type="button" class="landing__nav-link" @click="scrollTo('events')">Events</button>
          <button type="button" class="landing__nav-link" @click="scrollTo('about')">About</button>
          <button
            type="button"
            class="landing__theme"
            :aria-label="isDark ? 'Switch to light mode' : 'Switch to dark mode'"
            :title="isDark ? 'Light mode' : 'Dark mode'"
            @click="setMode(isDark ? 'light' : 'dark')"
          >
            <v-icon size="18">{{ isDark ? 'mdi-white-balance-sunny' : 'mdi-weather-night' }}</v-icon>
          </button>
          <v-btn color="primary" size="small" @click="portalOpen = true">Sign in</v-btn>
        </nav>
      </div>
    </header>

    <main>
      <!-- Hero -->
      <section class="landing__hero">
        <div class="landing__container landing__hero-grid">
          <div class="landing__hero-text">
            <UiBadge variant="outline" class="landing__eyebrow">
              <v-icon size="14">mdi-school-outline</v-icon>
              Department of Education<span v-if="branding.depedSchoolId"> · School ID {{ branding.depedSchoolId }}</span>
            </UiBadge>
            <h1 class="landing__title">{{ branding.name }}</h1>
            <p class="landing__lead">
              {{ branding.tagline || 'Class records, attendance and school updates for teachers and parents, in one place.' }}
            </p>
            <div class="landing__cta">
              <v-btn color="primary" size="large" append-icon="mdi-arrow-right" @click="portalOpen = true">
                Open portal
              </v-btn>
              <v-btn variant="outlined" size="large" @click="scrollTo('events')">View events</v-btn>
            </div>
            <ul class="landing__highlights">
              <li v-for="h in HIGHLIGHTS" :key="h.title">
                <span class="landing__highlight-icon"><v-icon size="18">{{ h.icon }}</v-icon></span>
                <span>
                  <strong>{{ h.title }}</strong>
                  <span class="landing__muted">{{ h.text }}</span>
                </span>
              </li>
            </ul>
          </div>
          <div class="landing__hero-media">
            <img :src="heroPhoto" alt="" class="landing__hero-img" />
          </div>
        </div>
      </section>

      <!-- School announcements -->
      <section v-if="leadNotice" id="announcements" class="landing__section">
        <div class="landing__container">
          <div class="landing__section-head">
            <h2 class="landing__h2">Announcements</h2>
            <p class="landing__muted">News and reminders from the school.</p>
          </div>
          <div class="landing__notices" :class="{ 'landing__notices--single': !otherNotices.length }">
            <button type="button" class="landing__lead-notice" @click="openAnnouncement(leadNotice)">
              <span class="landing__notice-head">
                <span class="landing__highlight-icon"><v-icon size="18">mdi-bullhorn-outline</v-icon></span>
                <span class="landing__event-meta">
                  <UiBadge v-if="leadNotice.pinned" variant="blue">
                    <v-icon size="13">mdi-pin-outline</v-icon>
                    Pinned
                  </UiBadge>
                  <UiBadge v-if="isNew(leadNotice)" variant="green">New</UiBadge>
                </span>
              </span>
              <span class="landing__lead-notice-title">{{ leadNotice.title }}</span>
              <span class="landing__lead-notice-body">{{ leadNotice.body }}</span>
              <span class="landing__notice-foot">
                <span class="landing__notice-date">{{ formatDate(leadNotice.createdAt, 'MMMM D, YYYY') }}</span>
                <span class="landing__read-more">
                  Read more
                  <v-icon size="16">mdi-arrow-right</v-icon>
                </span>
              </span>
            </button>

            <div v-if="otherNotices.length" class="landing__notice-list">
              <ul>
                <li v-for="a in otherNotices" :key="a.id">
                  <button type="button" class="landing__notice-row" @click="openAnnouncement(a)">
                    <span class="landing__notice-row-date" aria-hidden="true">
                      <span class="landing__date-month">{{ formatDate(a.createdAt, 'MMM') }}</span>
                      <span class="landing__date-day">{{ formatDate(a.createdAt, 'D') }}</span>
                    </span>
                    <span class="landing__notice-row-text">
                      <span class="landing__notice-row-title">
                        <v-icon v-if="a.pinned" size="14" class="landing__pin" aria-label="Pinned">mdi-pin</v-icon>
                        {{ a.title }}
                        <UiBadge v-if="isNew(a)" variant="green">New</UiBadge>
                      </span>
                      <span class="landing__notice-row-body">{{ a.body }}</span>
                    </span>
                  </button>
                </li>
              </ul>
              <v-btn
                v-if="hiddenNotices"
                variant="text"
                size="small"
                class="landing__notice-more"
                :append-icon="showAllNotices ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                @click="showAllNotices = !showAllNotices"
              >
                {{ showAllNotices ? 'Show fewer' : `Show all (${hiddenNotices} more)` }}
              </v-btn>
            </div>
          </div>
        </div>
      </section>

      <!-- Events -->
      <section id="events" class="landing__section landing__section--muted">
        <div class="landing__container">
          <div class="landing__section-head">
            <h2 class="landing__h2">Events</h2>
            <p class="landing__muted">What's coming up around campus.</p>
          </div>

          <v-skeleton-loader v-if="loadingEvents" type="image, article" class="landing__skeleton" />
          <UiEmptyState
            v-else-if="eventsFailed"
            icon="mdi-cloud-off-outline"
            title="Events couldn't be loaded"
            description="Please try again in a moment."
          />
          <template v-else>
            <UiEmptyState
              v-if="!upcoming.length"
              icon="mdi-calendar-blank-outline"
              title="No upcoming events"
              description="New events will appear here once the school posts them."
            />
            <div
              v-else
              class="landing__featured"
              role="region"
              aria-roledescription="carousel"
              aria-label="Upcoming events"
              @mouseenter="hovering = true"
              @mouseleave="hovering = false"
              @focusin="hovering = true"
              @focusout="hovering = false"
            >
              <v-carousel
                v-model="slide"
                :cycle="autoplay"
                :interval="6000"
                :height="smAndDown ? 560 : 380"
                :show-arrows="false"
                hide-delimiters
                continuous
                class="landing__carousel"
              >
                <v-carousel-item v-for="(event, i) in upcoming" :key="event.key" :value="i">
                  <article
                    class="landing__slide"
                    :aria-label="`${i + 1} of ${upcoming.length}: ${event.eventName}`"
                  >
                    <div class="landing__slide-media">
                      <img v-if="event.eventFile" :src="event.eventFile" alt="" />
                      <span v-else class="landing__slide-placeholder">
                        <v-icon size="56">mdi-calendar-star-outline</v-icon>
                      </span>
                    </div>
                    <div class="landing__slide-body">
                      <div class="landing__slide-when">
                        <span class="landing__date-block" aria-hidden="true">
                          <span class="landing__date-month">{{ formatDate(event.eventDate, 'MMM') }}</span>
                          <span class="landing__date-day">{{ formatDate(event.eventDate, 'D') }}</span>
                        </span>
                        <span>
                          <UiBadge variant="blue">{{ untilLabel(event.eventDate) }}</UiBadge>
                          <span class="landing__slide-date">{{ formatDate(event.eventDate, 'dddd, MMMM D, YYYY') }}</span>
                        </span>
                      </div>
                      <h3 class="landing__slide-title">{{ event.eventName }}</h3>
                      <p v-if="event.eventDescription" class="landing__slide-desc">{{ event.eventDescription }}</p>
                      <div class="landing__slide-actions">
                        <v-btn color="primary" append-icon="mdi-arrow-right" @click="openEvent(event)">
                          View details
                        </v-btn>
                      </div>
                    </div>
                  </article>
                </v-carousel-item>
              </v-carousel>

              <div v-if="upcoming.length > 1" class="landing__carousel-bar">
                <div class="landing__dots">
                  <button
                    v-for="(event, i) in upcoming"
                    :key="event.key"
                    type="button"
                    class="landing__dot"
                    :class="{ 'landing__dot--active': slide === i }"
                    :aria-label="`Show ${event.eventName}`"
                    :aria-current="slide === i ? 'true' : undefined"
                    @click="slide = i"
                  />
                </div>
                <div class="landing__carousel-nav">
                  <span class="landing__muted landing__carousel-count">{{ slide + 1 }} / {{ upcoming.length }}</span>
                  <v-btn icon="mdi-chevron-left" variant="outlined" size="small" aria-label="Previous event" @click="step(-1)" />
                  <v-btn icon="mdi-chevron-right" variant="outlined" size="small" aria-label="Next event" @click="step(1)" />
                </div>
              </div>
            </div>

            <template v-if="pastEvents.length">
              <h3 class="landing__subhead">Past events</h3>
              <ul class="landing__past">
                <li v-for="event in pastEvents" :key="event.key">
                  <button type="button" class="landing__past-card" @click="openEvent(event)">
                    <span class="landing__past-media">
                      <img v-if="event.eventFile" :src="event.eventFile" alt="" loading="lazy" />
                      <v-icon v-else size="24">mdi-calendar-check-outline</v-icon>
                    </span>
                    <span class="landing__past-text">
                      <span class="landing__past-date">{{ formatDate(event.eventDate, 'MMM D, YYYY') }}</span>
                      <span class="landing__past-title">{{ event.eventName }}</span>
                    </span>
                  </button>
                </li>
              </ul>
            </template>
          </template>
        </div>
      </section>

      <!-- About -->
      <section id="about" class="landing__section">
        <div class="landing__container">
          <div class="landing__section-head">
            <h2 class="landing__h2">Our mission and values</h2>
            <p class="landing__muted">Guided by the Department of Education.</p>
          </div>
          <div class="landing__statements">
            <article class="landing__statement">
              <span class="landing__statement-label">
                <span class="landing__highlight-icon"><v-icon size="18">mdi-flag-outline</v-icon></span>
                Mission
              </span>
              <p class="landing__statement-text">{{ MISSION }}</p>
            </article>
            <article class="landing__statement">
              <span class="landing__statement-label">
                <span class="landing__highlight-icon"><v-icon size="18">mdi-eye-outline</v-icon></span>
                Vision
              </span>
              <p class="landing__statement-text landing__statement-quote">{{ VISION }}</p>
            </article>
          </div>
          <h3 class="landing__subhead">Core values</h3>
          <ul class="landing__values">
            <li v-for="v in CORE_VALUES" :key="v.name" class="landing__value">
              <span class="landing__value-icon"><v-icon size="22">{{ v.icon }}</v-icon></span>
              <span class="landing__value-name">{{ v.name }}</span>
              <span class="landing__muted landing__value-meaning">{{ v.meaning }}</span>
            </li>
          </ul>
        </div>
      </section>
    </main>

    <footer class="landing__footer">
      <div class="landing__container landing__footer-inner">
        <div class="landing__brand">
          <img :src="branding.logoUrl" alt="" class="landing__logo" />
          <span>
            <span class="landing__brand-name">{{ branding.name }}</span>
            <span v-if="place" class="landing__muted landing__footer-place">{{ place }}</span>
          </span>
        </div>
        <p class="landing__muted landing__footer-copy">© {{ year }} {{ branding.shortName || branding.name }}</p>
      </div>
    </footer>

    <UiDialog
      v-model="portalOpen"
      title="Choose your portal"
      description="Pick the one that applies to you to sign in or create an account."
      size="lg"
      :persistent="false"
    >
      <UiChoiceCards :items="PORTALS" @select="openPortal" />
    </UiDialog>

    <UiDialog
      v-model="readingOpen"
      :title="reading?.title || 'Announcement'"
      :description="reading ? formatDate(reading.createdAt, 'dddd, MMMM D, YYYY') : ''"
      :persistent="false"
    >
      <p v-if="reading" class="landing__detail-text">{{ reading.body }}</p>
      <template #footer="{ close }">
        <v-btn variant="outlined" @click="close">Close</v-btn>
      </template>
    </UiDialog>

    <UiDialog
      v-model="detailOpen"
      :title="selected?.eventName || 'Event'"
      :description="selected ? formatDate(selected.eventDate, 'dddd, MMMM D, YYYY') : ''"
      :persistent="false"
    >
      <template v-if="selected">
        <img v-if="selected.eventFile" :src="selected.eventFile" alt="" class="landing__detail-img" />
        <div class="landing__event-meta">
          <UiBadge v-if="selected.past" variant="outline">Past</UiBadge>
          <UiBadge v-else variant="blue">{{ untilLabel(selected.eventDate) }}</UiBadge>
        </div>
        <p class="landing__detail-text">{{ selected.eventDescription || 'No details were added for this event.' }}</p>
      </template>
      <template #footer="{ close }">
        <v-btn variant="outlined" @click="close">Close</v-btn>
      </template>
    </UiDialog>
  </div>
</template>

<style scoped>
.landing {
  min-height: 100vh;
  background: var(--background);
  color: var(--foreground);
}
.landing__container {
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
  padding: 0 24px;
}
.landing__muted {
  color: var(--muted-foreground);
}

/* Top bar */
.landing__bar {
  position: sticky;
  top: 0;
  z-index: 10;
  border-bottom: 1px solid var(--border);
  background: color-mix(in srgb, var(--background) 85%, transparent);
  backdrop-filter: blur(8px);
}
.landing__bar-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  height: 60px;
}
.landing__brand {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  color: inherit;
  text-decoration: none;
}
.landing__logo {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  object-fit: contain;
}
.landing__brand-name {
  display: block;
  font-weight: 600;
  font-size: 15px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.landing__nav {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}
.landing__nav-link {
  padding: 6px 12px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--muted-foreground);
  font: inherit;
  font-size: 14px;
  cursor: pointer;
}
.landing__nav-link:hover,
.landing__nav-link:focus-visible {
  outline: none;
  background: var(--accent);
  color: var(--foreground);
}
/* Stays visible on phones, where the section links are hidden */
.landing__theme {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  margin-right: 4px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--muted-foreground);
  cursor: pointer;
}
.landing__theme:hover,
.landing__theme:focus-visible {
  outline: none;
  background: var(--accent);
  color: var(--foreground);
}

/* Hero */
.landing__hero {
  padding: 72px 0;
}
.landing__hero-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  align-items: center;
  gap: 48px;
}
.landing__eyebrow {
  gap: 6px;
}
.landing__title {
  margin: 16px 0 0;
  font-size: clamp(30px, 4.5vw, 46px);
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -0.02em;
}
.landing__lead {
  margin: 16px 0 0;
  max-width: 520px;
  font-size: 17px;
  line-height: 1.6;
  color: var(--muted-foreground);
}
.landing__cta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 28px;
}
.landing__highlights {
  display: grid;
  gap: 14px;
  margin: 36px 0 0;
  padding: 0;
  list-style: none;
}
.landing__highlights li {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 14px;
}
.landing__highlights strong {
  display: block;
  font-weight: 600;
}
.landing__highlight-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  border-radius: 8px;
  background: var(--brand-soft);
  color: var(--brand);
}
.landing__hero-img {
  display: block;
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  border: 1px solid var(--border);
  border-radius: 14px;
}

/* Sections */
.landing__section {
  padding: 72px 0;
  border-top: 1px solid var(--border);
}
.landing__section--muted {
  background: var(--subtle);
}
.landing__section-head {
  margin-bottom: 28px;
}
.landing__h2 {
  margin: 0 0 6px;
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.01em;
}
.landing__section-head p {
  margin: 0;
}

/* Announcements */
.landing__notices {
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr);
  gap: 20px;
  align-items: start;
}
.landing__notices--single {
  grid-template-columns: minmax(0, 1fr);
}
.landing__lead-notice {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  min-height: 100%;
  padding: 24px;
  border: 1px solid var(--border);
  border-top: 3px solid var(--brand);
  border-radius: 14px;
  background: var(--card);
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.landing__lead-notice:hover {
  box-shadow: 0 8px 24px -12px rgba(0, 0, 0, 0.18);
}
.landing__lead-notice:focus-visible,
.landing__notice-row:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--ring-halo);
}
.landing__notice-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.landing__lead-notice-title {
  display: block;
  margin: 4px 0 0;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.3;
  letter-spacing: -0.01em;
}
.landing__lead-notice-body {
  margin: 0;
  display: block;
  font-size: 15px;
  line-height: 1.65;
  color: var(--muted-foreground);
  white-space: pre-line;
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.landing__notice-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: auto;
  padding-top: 8px;
}
.landing__notice-date {
  font-size: 13px;
  color: var(--muted-foreground);
}
.landing__read-more {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  font-weight: 600;
  color: var(--brand);
}
.landing__notice-list {
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 14px;
  background: var(--card);
}
.landing__notice-list ul {
  margin: 0;
  padding: 0;
  list-style: none;
}
.landing__notice-list li + li {
  border-top: 1px solid var(--border);
}
.landing__notice-row {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  width: 100%;
  padding: 14px 16px;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s ease;
}
.landing__notice-row:hover {
  background: var(--accent);
}
.landing__notice-row-date {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  width: 44px;
  flex-shrink: 0;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--background);
}
.landing__notice-row-date .landing__date-month {
  font-size: 10px;
}
.landing__notice-row-date .landing__date-day {
  font-size: 17px;
}
.landing__notice-row-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.landing__notice-row-title {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
}
.landing__pin {
  color: var(--brand);
}
.landing__notice-row-body {
  font-size: 13px;
  color: var(--muted-foreground);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.landing__notice-more {
  width: 100%;
  border-top: 1px solid var(--border);
  border-radius: 0;
}

/* Events */
.landing__skeleton {
  border-radius: 14px;
}

/* Upcoming events carousel */
.landing__featured {
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 14px;
  background: var(--card);
}
.landing__slide {
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
  height: 100%;
}
.landing__slide-media {
  overflow: hidden;
  background: linear-gradient(135deg, var(--brand-soft), color-mix(in srgb, var(--brand) 22%, var(--card)));
}
.landing__slide-media img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.landing__slide-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--brand);
}
.landing__slide-body {
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-height: 0;
  padding: 32px;
}
.landing__slide-when {
  display: flex;
  align-items: center;
  gap: 14px;
}
.landing__date-block {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  width: 56px;
  flex-shrink: 0;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--background);
}
.landing__date-month {
  width: 100%;
  padding: 2px 0;
  background: var(--brand);
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-align: center;
  text-transform: uppercase;
}
.landing__date-day {
  padding: 4px 0 6px;
  font-size: 22px;
  font-weight: 700;
  line-height: 1;
}
.landing__slide-date {
  display: block;
  margin-top: 6px;
  font-size: 13px;
  color: var(--muted-foreground);
}
.landing__slide-title {
  margin: 0;
  font-size: clamp(20px, 2.4vw, 26px);
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: -0.01em;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.landing__slide-desc {
  margin: 0;
  font-size: 15px;
  line-height: 1.6;
  color: var(--muted-foreground);
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.landing__slide-actions {
  margin-top: auto;
}
.landing__carousel-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 16px;
  border-top: 1px solid var(--border);
}
.landing__dots {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.landing__dot {
  width: 8px;
  height: 8px;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: var(--border-strong);
  cursor: pointer;
  transition: width 0.2s ease, background-color 0.2s ease;
}
.landing__dot--active {
  width: 22px;
  background: var(--brand);
}
.landing__dot:focus-visible {
  outline: 2px solid var(--brand);
  outline-offset: 2px;
}
.landing__carousel-nav {
  display: flex;
  align-items: center;
  gap: 8px;
}
.landing__carousel-count {
  margin-right: 4px;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}

/* Past events strip */
.landing__subhead {
  margin: 36px 0 14px;
  font-size: 15px;
  font-weight: 600;
}
.landing__past {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(240px, 280px);
  gap: 12px;
  margin: 0;
  padding: 0 0 8px;
  list-style: none;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
}
.landing__past li {
  scroll-snap-align: start;
}
.landing__past-card {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--card);
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease;
}
.landing__past-card:hover {
  border-color: var(--border-strong);
}
.landing__past-card:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--ring-halo);
}
.landing__past-media {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  flex-shrink: 0;
  overflow: hidden;
  border-radius: 8px;
  background: var(--brand-soft);
  color: var(--brand);
}
.landing__past-media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.landing__past-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.landing__past-date {
  font-size: 12px;
  color: var(--muted-foreground);
}
.landing__past-title {
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.landing__event-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
/* About */
.landing__statements {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 20px;
}
.landing__statement {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 28px;
  border: 1px solid var(--border);
  border-radius: 14px;
  background: linear-gradient(160deg, color-mix(in srgb, var(--brand) 14%, var(--card)), var(--card) 75%);
}
.landing__statement-label {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--muted-foreground);
}
.landing__statement-text {
  margin: 0;
  font-size: 15px;
  line-height: 1.75;
}
.landing__statement-quote {
  font-size: 17px;
  font-weight: 500;
  line-height: 1.7;
}
.landing__values {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.landing__value {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 22px 16px;
  border: 1px solid var(--border);
  border-radius: 14px;
  background: var(--card);
  text-align: center;
}
.landing__value-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  margin-bottom: 6px;
  border-radius: 999px;
  background: var(--brand-soft);
  color: var(--brand);
}
.landing__value-name {
  font-size: 16px;
  font-weight: 700;
}
.landing__value-meaning {
  font-size: 13px;
}

/* Footer */
.landing__footer {
  border-top: 1px solid var(--border);
  padding: 28px 0;
}
.landing__footer-inner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.landing__footer-place {
  display: block;
  font-size: 13px;
}
.landing__footer-copy {
  margin: 0;
  font-size: 13px;
}

/* Event details dialog */
.landing__detail-img {
  display: block;
  width: 100%;
  max-height: 320px;
  margin-bottom: 16px;
  object-fit: cover;
  border: 1px solid var(--border);
  border-radius: 10px;
}
.landing__detail-text {
  margin: 12px 0 0;
  font-size: 14px;
  line-height: 1.7;
  white-space: pre-line;
}

@media (max-width: 960px) {
  .landing__hero-grid,
  .landing__notices,
  .landing__statements {
    grid-template-columns: minmax(0, 1fr);
  }
  .landing__values {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .landing__hero {
    padding: 48px 0;
  }
  .landing__hero-media {
    order: -1;
  }
  .landing__section {
    padding: 48px 0;
  }
  .landing__slide {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: 220px minmax(0, 1fr);
  }
  .landing__slide-body {
    padding: 20px;
  }
}
@media (max-width: 600px) {
  .landing__container {
    padding: 0 16px;
  }
  .landing__nav-link {
    display: none;
  }
  .landing__cta .v-btn {
    flex: 1 1 100%;
  }
}
</style>
